import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useGlobalConfig } from '../hooks/useGlobalConfig';
import { useEntitlements } from '../contexts/EntitlementsContext';
import {
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  CogIcon,
  CheckIcon,
  XMarkIcon,
  CreditCardIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import BillingSettings from '../components/billing/BillingSettings';
import SupportSettings from '../components/support/SupportSettings';
import {
  systemSettingsApi,
  FacilityInfo,
  CurrencyInfo,
  SystemInfo,
  UpdateSystemSettingsDto,
  TIMEZONE_OPTIONS,
  CURRENCY_OPTIONS,
  DATE_FORMAT_OPTIONS,
  LANGUAGE_OPTIONS,
} from '../api/system-settings';
import PageHero from '../components/ui/PageHero';

interface SystemSettingsForm {
  // Facility Information
  facilityName: string;
  facilityAddress: string;
  facilityCity: string;
  facilityState: string;
  facilityPostalCode: string;
  facilityCountry: string;
  facilityPhone: string;
  facilityEmail: string;
  facilityWebsite: string;
  facilityId: string;
  
  // Currency Settings
  currencyDefault: string;
  currencySymbol: string;
  
  // System Settings
  systemTimezone: string;
  systemDateFormat: string;
  systemLanguage: string;
}

const SETTINGS_TABS = new Set(['facility', 'currency', 'system', 'billing', 'support']);

export default function SystemSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(
    tabFromUrl && SETTINGS_TABS.has(tabFromUrl) ? tabFromUrl : 'facility',
  );
  const { refreshConfig } = useGlobalConfig();
  const { refresh: refreshEntitlements } = useEntitlements();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
    reset,
  } = useForm<SystemSettingsForm>();

  const watchCurrency = watch('currencyDefault');

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && SETTINGS_TABS.has(tab)) {
      setActiveTab(tab);
    }
    const checkout = searchParams.get('checkout');
    const paystackRef = searchParams.get('trxref') || searchParams.get('reference');
    if (checkout === 'success' || paystackRef) {
      setActiveTab('billing');
      toast.success('Payment received. Subscription will update in a few seconds.');
      searchParams.delete('checkout');
      searchParams.delete('trxref');
      searchParams.delete('reference');
      searchParams.set('tab', 'billing');
      setSearchParams(searchParams, { replace: true });
      void refreshEntitlements();
    } else if (checkout === 'cancelled') {
      toast('Checkout cancelled — no changes made');
      searchParams.delete('checkout');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refreshEntitlements]);

  // Update currency symbol when currency changes
  useEffect(() => {
    if (watchCurrency) {
      const currency = CURRENCY_OPTIONS.find(c => c.value === watchCurrency);
      if (currency) {
        setValue('currencySymbol', currency.symbol);
      }
    }
  }, [watchCurrency, setValue]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [facilityResponse, currencyResponse, systemResponse] = await Promise.all([
        systemSettingsApi.getFacilityInfo(),
        systemSettingsApi.getCurrencyInfo(),
        systemSettingsApi.getSystemInfo(),
      ]);

      const facility = facilityResponse.data;
      const currency = currencyResponse.data;
      const system = systemResponse.data;

      reset({
        facilityName: facility.name || '',
        facilityAddress: facility.address || '',
        facilityCity: facility.city || '',
        facilityState: facility.state || '',
        facilityPostalCode: facility.postalCode || '',
        facilityCountry: facility.country || '',
        facilityPhone: facility.phone || '',
        facilityEmail: facility.email || '',
        facilityWebsite: facility.website || '',
        facilityId: facility.facilityId || '',
        currencyDefault: currency.default || 'ZAR',
        currencySymbol: currency.symbol || 'R',
        systemTimezone: system.timezone || 'Africa/Johannesburg',
        systemDateFormat: system.dateFormat || 'DD/MM/YYYY',
        systemLanguage: system.language || 'en',
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: SystemSettingsForm) => {
    try {
      setSaving(true);

      const updateData: UpdateSystemSettingsDto = {
        'facility.name': data.facilityName,
        'facility.address': data.facilityAddress,
        'facility.city': data.facilityCity,
        'facility.state': data.facilityState,
        'facility.postalCode': data.facilityPostalCode,
        'facility.country': data.facilityCountry,
        'facility.phone': data.facilityPhone,
        'facility.email': data.facilityEmail,
        'facility.website': data.facilityWebsite,
        'facility.facilityId': data.facilityId,
        'currency.default': data.currencyDefault,
        'currency.symbol': data.currencySymbol,
        'system.timezone': data.systemTimezone,
        'system.dateFormat': data.systemDateFormat,
        'system.language': data.systemLanguage,
      };

      await systemSettingsApi.updateBulk(updateData);
      toast.success('System settings updated successfully');
      reset(data); // Reset form state to mark as not dirty
      
      // Refresh global configuration to apply changes system-wide
      await refreshConfig();
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update system settings');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    {
      id: 'facility',
      name: 'Facility Information',
      icon: BuildingOfficeIcon,
    },
    {
      id: 'currency',
      name: 'Currency & Localization',
      icon: CurrencyDollarIcon,
    },
    {
      id: 'system',
      name: 'System Preferences',
      icon: CogIcon,
    },
    {
      id: 'billing',
      name: 'Subscription & Billing',
      icon: CreditCardIcon,
    },
    {
      id: 'support',
      name: 'Help & Support',
      icon: ChatBubbleLeftRightIcon,
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHero
        title="System Settings"
        subtitle="Manage system-wide settings and configuration"
        actions={
          isDirty ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => loadSettings()} className="btn btn-secondary text-sm focus-pop">
                <XMarkIcon className="h-4 w-4" />
                Cancel
              </button>
              <button type="submit" form="settings-form" disabled={saving} className="btn btn-primary text-sm focus-pop">
                {saving ? (
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent" />
                ) : (
                  <CheckIcon className="h-4 w-4" />
                )}
                Save Changes
              </button>
            </div>
          ) : undefined
        }
      />

      <div className="bg-white shadow rounded-lg">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setSearchParams({ tab: tab.id }, { replace: true });
                }}
                className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
                  }
                `}
              >
                <tab.icon
                  className={`
                    -ml-0.5 mr-2 h-5 w-5
                    ${activeTab === tab.id ? 'text-primary-500' : 'text-slate-400 group-hover:text-slate-500'}
                  `}
                />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Form Content — facility / currency / system only. Billing is not a form. */}
        <form
          id="settings-form"
          onSubmit={handleSubmit(onSubmit)}
          className={activeTab === 'billing' || activeTab === 'support' ? 'hidden' : 'p-6'}
        >
          {/* Facility Information Tab */}
          {activeTab === 'facility' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
                  Breeder Facility Information
                </h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="facilityName" className="block text-sm font-medium text-slate-700">
                      Facility Name *
                    </label>
                    <input
                      type="text"
                      {...register('facilityName', { required: 'Facility name is required' })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Enter your breeding facility name"
                    />
                    {errors.facilityName && (
                      <p className="mt-1 text-sm text-red-600">{errors.facilityName.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="facilityId" className="block text-sm font-medium text-slate-700">
                      Facility License Number
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                      <input
                        type="text"
                        {...register('facilityId')}
                        readOnly
                        className="block w-full border-gray-300 rounded-l-md shadow-sm bg-slate-50/80 text-slate-500 focus:ring-cyan-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Auto-generated facility ID"
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const response = await systemSettingsApi.regenerateFacilityId();
                            toast.success('Facility ID regenerated successfully');
                            // Reload settings to get the new ID
                            await loadSettings();
                          } catch (error) {
                            toast.error('Failed to regenerate facility ID');
                          }
                        }}
                        className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-slate-50/80 text-sm font-medium text-slate-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                      >
                        Regenerate
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      This unique identifier is automatically generated and used for marketplace integration.
                    </p>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="facilityAddress" className="block text-sm font-medium text-slate-700">
                      Address
                    </label>
                    <input
                      type="text"
                      {...register('facilityAddress')}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Street address"
                    />
                  </div>

                  <div>
                    <label htmlFor="facilityCity" className="block text-sm font-medium text-slate-700">
                      City
                    </label>
                    <input
                      type="text"
                      {...register('facilityCity')}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-primary-500 sm:text-sm"
                      placeholder="City"
                    />
                  </div>

                  <div>
                    <label htmlFor="facilityState" className="block text-sm font-medium text-slate-700">
                      State/Province
                    </label>
                    <input
                      type="text"
                      {...register('facilityState')}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-primary-500 sm:text-sm"
                      placeholder="State or province"
                    />
                  </div>

                  <div>
                    <label htmlFor="facilityPostalCode" className="block text-sm font-medium text-slate-700">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      {...register('facilityPostalCode')}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Postal or ZIP code"
                    />
                  </div>

                  <div>
                    <label htmlFor="facilityCountry" className="block text-sm font-medium text-slate-700">
                      Country
                    </label>
                    <input
                      type="text"
                      {...register('facilityCountry')}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Country"
                    />
                  </div>

                  <div>
                    <label htmlFor="facilityPhone" className="block text-sm font-medium text-slate-700">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      {...register('facilityPhone')}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Phone number"
                    />
                  </div>

                  <div>
                    <label htmlFor="facilityEmail" className="block text-sm font-medium text-slate-700">
                      Email Address
                    </label>
                    <input
                      type="email"
                      {...register('facilityEmail', {
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Email address"
                    />
                    {errors.facilityEmail && (
                      <p className="mt-1 text-sm text-red-600">{errors.facilityEmail.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="facilityWebsite" className="block text-sm font-medium text-slate-700">
                      Website
                    </label>
                    <input
                      type="url"
                      {...register('facilityWebsite')}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-primary-500 sm:text-sm"
                      placeholder="https://www.example.com"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Currency & Localization Tab */}
          {activeTab === 'currency' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
                  Currency & Localization Settings
                </h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="currencyDefault" className="block text-sm font-medium text-slate-700">
                      Default Currency *
                    </label>
                    <select
                      {...register('currencyDefault', { required: 'Currency is required' })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-primary-500 sm:text-sm"
                    >
                      {CURRENCY_OPTIONS.map((currency) => (
                        <option key={currency.value} value={currency.value}>
                          {currency.label}
                        </option>
                      ))}
                    </select>
                    {errors.currencyDefault && (
                      <p className="mt-1 text-sm text-red-600">{errors.currencyDefault.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="currencySymbol" className="block text-sm font-medium text-slate-700">
                      Currency Symbol
                    </label>
                    <input
                      type="text"
                      {...register('currencySymbol')}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Currency symbol"
                      maxLength={5}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* System Preferences Tab */}
          {activeTab === 'system' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
                  System Preferences
                </h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="systemTimezone" className="block text-sm font-medium text-slate-700">
                      Timezone *
                    </label>
                    <select
                      {...register('systemTimezone', { required: 'Timezone is required' })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-primary-500 sm:text-sm"
                    >
                      {TIMEZONE_OPTIONS.map((timezone) => (
                        <option key={timezone.value} value={timezone.value}>
                          {timezone.label}
                        </option>
                      ))}
                    </select>
                    {errors.systemTimezone && (
                      <p className="mt-1 text-sm text-red-600">{errors.systemTimezone.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="systemDateFormat" className="block text-sm font-medium text-slate-700">
                      Date Format *
                    </label>
                    <select
                      {...register('systemDateFormat', { required: 'Date format is required' })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-primary-500 sm:text-sm"
                    >
                      {DATE_FORMAT_OPTIONS.map((format) => (
                        <option key={format.value} value={format.value}>
                          {format.label}
                        </option>
                      ))}
                    </select>
                    {errors.systemDateFormat && (
                      <p className="mt-1 text-sm text-red-600">{errors.systemDateFormat.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="systemLanguage" className="block text-sm font-medium text-slate-700">
                      Language *
                    </label>
                    <select
                      {...register('systemLanguage', { required: 'Language is required' })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-primary-500 sm:text-sm"
                    >
                      {LANGUAGE_OPTIONS.map((language) => (
                        <option key={language.value} value={language.value}>
                          {language.label}
                        </option>
                      ))}
                    </select>
                    {errors.systemLanguage && (
                      <p className="mt-1 text-sm text-red-600">{errors.systemLanguage.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>

        {activeTab === 'billing' && (
          <div className="p-6">
            <BillingSettings />
          </div>
        )}

        {activeTab === 'support' && (
          <div className="p-6">
            <SupportSettings />
          </div>
        )}
      </div>
    </div>
  );
} 